import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    isTerminalJobStatus,
    provisioningService,
    type ProvisioningEvent,
    type ProvisioningJobStatus,
    type ProvisioningStatusPush,
} from '@/services/provisioning';
import { deploymentKeys } from './deployment.keys';
import { useProvisioningSocket } from './use-provisioning-socket';

const MAX_EVENTS = 5000;
const TRIM_TO = 4000;
const POLL_MS = 5000;

interface UseProvisioningLogResult {
    events: ProvisioningEvent[];
    status: ProvisioningJobStatus | undefined;
    verifyAttempt: number | undefined;
    isTerminal: boolean;
    truncated: boolean;
    isLoading: boolean;
    error: string | null;
    isConnected: boolean;
}

/**
 * The provisioning log for one job: seeded and repaired over REST, extended
 * live over the socket.
 *
 * Two design points that matter:
 *
 * - Events are held in a Map keyed by `counter`, so de-duplication is
 *   structural rather than a filter pass. Central delivers callbacks
 *   at-least-once and the poll overlaps the socket, so duplicates are the
 *   normal case, not an edge case.
 * - `counter` is a dense sequence assigned by the provisioner, so a gap means
 *   something was genuinely lost. On a gap we rewind `after` to the last
 *   contiguous counter and refetch immediately rather than rendering a hole.
 */
export function useProvisioningLog(
    jobId: string | undefined,
    deploymentId: string | undefined,
    initialStatus?: ProvisioningJobStatus,
): UseProvisioningLogResult {
    const queryClient = useQueryClient();
    const eventsRef = useRef(new Map<number, ProvisioningEvent>());
    const afterRef = useRef(0);

    const [version, setVersion] = useState(0);
    const [status, setStatus] = useState<ProvisioningJobStatus | undefined>(initialStatus);
    const [verifyAttempt, setVerifyAttempt] = useState<number | undefined>();
    const [truncated, setTruncated] = useState(false);
    const [isLoading, setIsLoading] = useState(!!jobId);
    const [error, setError] = useState<string | null>(null);

    // Reset everything when the job changes — otherwise switching between
    // attempts in the history table interleaves two logs.
    useEffect(() => {
        eventsRef.current = new Map();
        afterRef.current = 0;
        setTruncated(false);
        setStatus(initialStatus);
        setVerifyAttempt(undefined);
        setIsLoading(!!jobId);
        setVersion((v) => v + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    const merge = useCallback((incoming: ProvisioningEvent[]) => {
        if (incoming.length === 0) return;
        const map = eventsRef.current;
        for (const event of incoming) map.set(event.counter, event);

        if (map.size > MAX_EVENTS) {
            const keys = [...map.keys()].sort((a, b) => a - b);
            for (const key of keys.slice(0, map.size - TRIM_TO)) map.delete(key);
            setTruncated(true);
        }

        // Advance the replay cursor only across a contiguous run, so a gap is
        // refetched rather than skipped.
        const sorted = [...map.keys()].sort((a, b) => a - b);
        let cursor = afterRef.current;
        for (const counter of sorted) {
            if (counter <= cursor) continue;
            if (counter === cursor + 1) cursor = counter;
            else break;
        }
        afterRef.current = cursor;
        setVersion((v) => v + 1);
    }, []);

    const fetchPage = useCallback(async () => {
        if (!jobId) return;
        try {
            const page = await provisioningService.getLogs(jobId, afterRef.current);
            setError(null);
            setStatus(page.status);
            merge(page.events);
            // A page that filled to the limit means there is more waiting.
            if (page.lastCounter > afterRef.current && page.events.length > 0) {
                const more = await provisioningService.getLogs(jobId, afterRef.current);
                merge(more.events);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load the log');
        } finally {
            setIsLoading(false);
        }
    }, [jobId, merge]);

    useEffect(() => {
        if (!jobId) return;
        void fetchPage();
    }, [jobId, fetchPage]);

    // Keep polling while the run is live. The socket can drop for good; a
    // half-hour operation cannot depend on it.
    useEffect(() => {
        if (!jobId || isTerminalJobStatus(status)) return;
        const timer = setInterval(() => void fetchPage(), POLL_MS);
        return () => clearInterval(timer);
    }, [jobId, status, fetchPage]);

    const onEvents = useCallback(
        (push: { events: ProvisioningEvent[] }) => merge(push.events),
        [merge],
    );

    const onStatus = useCallback(
        (push: ProvisioningStatusPush) => {
            setStatus(push.status);
            setVerifyAttempt(push.verifyAttempt);
            if (isTerminalJobStatus(push.status)) {
                // One last pull so the tail of the log is not lost to a status
                // callback that overtook the final event batch. The cache
                // invalidation is handled by the effect below, which covers the
                // poll path too.
                void fetchPage();
            }
        },
        [fetchPage],
    );

    // Refresh the deployment itself whenever the job reaches a terminal state,
    // whichever path observed it.
    //
    // This used to live inside onStatus, so it only ran when the socket
    // delivered the terminal push — and the socket is exactly what you do not
    // get when a job fails fast. Dispatch resolves, the panel mounts, and only
    // then does it subscribe with the new jobId; a run that dies in a few
    // hundred milliseconds has already emitted its status by that point. The
    // 5s poll then picked up the terminal status and updated the console, while
    // the deployment header kept rendering the status it was last told about —
    // a panel reading FAILED under a badge still reading PROVISIONING.
    //
    // Keying off `status` instead means it fires once per terminal transition
    // no matter who noticed first.
    useEffect(() => {
        if (!jobId || !isTerminalJobStatus(status)) return;
        queryClient.invalidateQueries({ queryKey: deploymentKeys.detail(deploymentId) });
        queryClient.invalidateQueries({
            queryKey: deploymentKeys.provisioningJobs(deploymentId),
        });
    }, [jobId, status, deploymentId, queryClient]);

    const { isConnected } = useProvisioningSocket(jobId, { onEvents, onStatus });

    const events = useMemo(
        () => [...eventsRef.current.values()].sort((a, b) => a.counter - b.counter),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [version],
    );

    return {
        events,
        status,
        verifyAttempt,
        isTerminal: isTerminalJobStatus(status),
        truncated,
        isLoading,
        error,
        isConnected,
    };
}
